import { AnimateModal, ModalOpenProps } from '@/components/ui/modal';

import { useBrushCorrectSetting } from '../hooks/useBrushCorrectSetting';
import { useNormalView } from '../hooks/useNormalView';
import { ImageRaidoButton, CreateMoreSystems } from '../inputs';
import { TKSystem, TKEvent } from '../types';

type BrushSettinProps = ModalOpenProps & {
  systems: TKSystem[];
};

/**
 * Brush Setting Modal
 * This modal allows users to set brush correction options such as symbol type, system, and color.
 * @log first created at 2024/04/12
 * @log restored to use in 2025/06/04
 * @returns
 */
export const BrushSettingModal = ({
  isOpen,
  isOnStage,
  lazyCloseHandler,
  systems,
}: BrushSettinProps) => {
  const { isNormalView, setIsNormalView, normalView, backToNormal } =
    useNormalView();
  const ubc = useBrushCorrectSetting(systems);

  const cancelBrushHandler = () => {
    lazyCloseHandler();
    document.dispatchEvent(new Event(TKEvent.CANCEL_BRUSH_TOOL));
  };

  return (
    <AnimateModal
      isOpen={isOpen}
      isOnStage={isOnStage}
      lazyCloseHandler={cancelBrushHandler}
      title={isNormalView ? 'Brush Correction Settings' : 'Select System'}
      containerClass="w-auto max-h-full "
      innerViewSwitchHandler={isNormalView ? undefined : backToNormal}
    >
      {/* DUCT - PIPE - PLUMBING */}
      <div className={normalView('flex justify-between my-0 w-80 mt-8 mb-4')}>
        <ImageRaidoButton
          label="Duct"
          value="DUCT"
          name="symbolType"
          selectedValue={ubc.categoryType}
          selectChangeHandler={ubc.categoryChangeHandler}
        />
        <ImageRaidoButton
          label="Pipe and Plumbing"
          value="PIPE|PLUMBING"
          name="symbolType"
          selectedValue={ubc.categoryType}
          selectChangeHandler={ubc.categoryChangeHandler}
        />
        {/* <ImageRaidoButton
          label="Plumbing"
          value="PLUMBING"
          name="symbolType"
          selectedValue={ubc.categoryType}
          selectChangeHandler={ubc.categoryChangeHandler}
        /> */}
      </div>
      {/* == `system` select == */}
      <CreateMoreSystems
        systems={systems}
        category={ubc.categoryType}
        itemType={'brush'}
        forceToNormal={isNormalView}
        currentSystem={ubc.selectedSystem}
        currentSystemChangeHandler={ubc.systemChangeHandler}
        onMoreSystemSwitch={setIsNormalView}
      />
      {/* == `zone` field input == */}
      <label className={normalView('color-primary text-xs block my-2 mb-2')}>
        ZONE
        <input
          type="text"
          value={ubc.zoneProperty}
          onChange={ubc.zoneChangeHandler}
          placeholder="Zone property"
          className="block w-full px-2 py-1 mt-1 text-sm frame-input focus-blue-ring-thick"
        />
      </label>
      {/* TODO: add more... */}
      {/* == size correction mode == */}
      <h2 className={normalView('color-primary text-sm  mt-4 mb-2')}>
        SIZE CORRECTION PATTERN
      </h2>
      <div className={normalView('flex my-0 w-64 mb-6')}>
        <ImageRaidoButton
          label="Line Tracing"
          value="line"
          name="brushType"
          selectedValue={ubc.brushType}
          selectChangeHandler={ubc.brushTypeChangeHandler}
        />
        {/* <ImageRaidoButton
          label="Circle Filling"
          value="circle"
          name="brushType"
          selectedValue={ubc.brushType}
          selectChangeHandler={ubc.brushTypeChangeHandler}
        /> */}
      </div>
      {/* == COLOR select == */}
      <h2 className={normalView('color-primary text-sm  mb-2')}>PAINT COLOR</h2>
      <div className={normalView('flex my-0 w-64 mb-8')}>
        {/* RED */}
        <ImageRaidoButton
          value="red"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
        {/* ORANGE */}
        <ImageRaidoButton
          value="orange"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
        {/* YELLOW */}
        <ImageRaidoButton
          value="yellow"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
        {/* GREEN */}
        <ImageRaidoButton
          value="green"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
        {/* PURPLE */}
        <ImageRaidoButton
          value="purple"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
        {/* BLUE */}
        <ImageRaidoButton
          value="blue"
          name="colorType"
          selectedValue={ubc.renderColor}
          selectChangeHandler={ubc.colorTypeChangeHandler}
        />
      </div>
      {/* button row */}
      <div className={normalView('flex gap-x-10 justify-between mx-0 mt-6')}>
        {/* close modal */}
        <button
          className="btn-red-border color-red font-semibold flex-1 red-shadow"
          onClick={cancelBrushHandler}
        >
          Cancel
        </button>
        {/* save button */}
        <button
          className="btn-green flex-1 leading-5 text-base bg-green shadow-md"
          onClick={() => {
            lazyCloseHandler();
            ubc.applyBrushSelectHandler();
          }}
        >
          Apply
        </button>
      </div>
    </AnimateModal>
  );
};
